import { AUTHOR_ROLE } from "../pages/chat/utils";
import { AccessProviderState } from "../state/AccessProvider";
import { DocumentItem } from "./document.model";

export type ChatRequest = {
  prompt: string
  sessionId: string
  documentId: string | null
  messageId?: string
  extraLoginInfo?: AccessProviderState["extraLoginInfo"]
  orchestrationType: OrchestrationType;
}
export type CompletedAgent = {
  agent: string;
  timestamp: string; // Use ISO string for DateTime
  completionConfidence: number;
  handoffReason: string;
};

export type UserIntent = {
  type: string; // e.g., "clarify", "agree", "object"
  targetAgent: string;
  confidence: number;
};

export type ThemeMode = 'light' | 'dark';


export type ConversationFlags = {
  backtrackRequested: boolean;
  userConfused: boolean;
  agentRecommendationPending: boolean;
};


// C# enum serializes as numbers: 0 = StartNegotiation, 1 = ContinueNegotiation, 2 = AdhocNegotiation, 3 = DeceptionAnalysis
export type OrchestrationType = 0 | 1 | 2 | 3;


export const OrchestrationType = {
  StartNegotiation: 0 as const,
  ContinueNegotiation: 1 as const,
  AdhocNegotiation: 2 as const,
  DeceptionAnalysis: 3 as const
};


export type Orchestration = {
  type: OrchestrationType;
  defaultAgent: string;
  currentAgent: string;
  previousAgent?: string | null;
  agentStack: string[];
  isComplete: boolean;
  handoffRecommendation: string;
};



export type OrchestrationState = {
  nextPrompt?: string | null;
  topicIndicesByAgent: { [agent: string]: number };
  completedAgents: CompletedAgent[];
  userIntent: UserIntent;
  topicCoverage: { [agent: string]: string[] };
  completionConfidenceByAgent: { [agent: string]: number };
  conversationFlags: ConversationFlags;
  orchestrations?: Orchestration[];
  currentOrchestrationType?: OrchestrationType;
};

// Utility function to get current orchestration from state
export function getCurrentOrchestration(state: OrchestrationState | null): Orchestration | null {
  if (!state?.orchestrations || state.orchestrations.length === 0) {
    return null;
  }
  

    // If currentOrchestrationType is set, find the last matching orchestration
  if (state.currentOrchestrationType !== undefined) {
    // Filter orchestrations by type and return the last one
    const matchingOrchestrations = state.orchestrations.filter(o => o.type === state.currentOrchestrationType);
    return matchingOrchestrations.length > 0 
      ? matchingOrchestrations[matchingOrchestrations.length - 1] 
      : null;
  }

  // Otherwise return the first incomplete orchestration or the last one
  return state.orchestrations.find(o => !o.isComplete) ?? state.orchestrations[state.orchestrations.length - 1];
}

export type ChatMessage = {
  messageId: string
  content: string
  authorRole: { label: AUTHOR_ROLE }
  timestamp: string
  agentName?: string // Agent name from backend Messenger field for assistant messages
}

export type Conversation = {
  sessionId: string | null,
  messages: ChatMessage[]
  attachments: DocumentItem[]
}

export type ChatHistoryDetail = {
  content: string;
  authorRole: { label: AUTHOR_ROLE };
  timestamp: Date;
};

export type ChatHistory = {
  attachments: { [key: string]: DocumentItem[] };
  chatHistory: { [key: string]: ChatMessage[] };
};

export type ChatResponse = {
  content: string;
};

export enum UploadingStatus {
  STANDBY = 'STANDBY',
  UPLOADING = 'UPLOADING',
  ANALYZING = 'ANALYZING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

//define app state interface
export interface AppState {
  isLoading: boolean;
  isChatHistoryOpen: boolean;
  frontendSettings: FrontendSettings | null;
  chatHistory: ChatHistory | null;
  isChatHistoryLoading: boolean;
  currentConversation: Conversation;
  currentOrchState: OrchestrationState | null; // <-- Add this line
  currentAgent: string | null;
  activeDocument: DocumentItem | null;
  uploadingStatus: UploadingStatus;
  isPhone: boolean;
  isChromeExtension: boolean;
  prompts: PromptConfig[];
  glossary?: GlossaryItem[]; // <-- Add glossary items
  subscriptionVerified?: boolean | null; // <-- Add this
  themeMode: ThemeMode; // <-- Add this
  shouldAddContinueOption: boolean;
  maxStep: number;
  currentOrchestrationType: OrchestrationType;
  isReadOnlyMode: boolean; // <-- Read-only mode for shared conversations
}

export interface PromptConfig {
  id?: string; // <-- add this
  title: string; // The title of the prompt
  prompt: string; // The content of the prompt (optional if not always present)
  version: string; // The version of the prompt
  isActive: boolean; // Indicates if the prompt is active or not
  contentBlobUrl?: string; // <-- add this
  agent: string;
  temp: string;
  llmModel?: string; // LLM model selection
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
  playgroundCreation?: boolean;
  playgroundDocuments?: string[]; // Blob URLs of documents for persistence
  playgroundDocumentId?: string; // Kernel Memory document ID for RAG
}

//create app context state object 
export type AppContextValue = {
  state: AppState;
  sendChatRequest: (sessionId: string | null, message: ChatMessage, documentId: string | null) => Promise<AbortController>;
  updateChatMessageRequest: (sessionId: string | null, message: ChatMessage, documentId: string | null) => Promise<AbortController>;
  clearCurrentChat: () => Promise<AbortController>;
  getChatHistory: () => Promise<void>;
  getConversationBySessionId: (sessionId: string) => Promise<void>;
  getOrchState: (sessionId: string) => Promise<OrchestrationState | null>; // <-- updated
  clearChatHistory: () => Promise<AbortController>;
  clearSessionHistory: (sessionId: string) => Promise<AbortController>;
  stopGenerating: () => void;
  isApproved: boolean | null;
  isAdmin: boolean | null;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean | null>>;
  isDeveloper: boolean | null;
  isAdvancedUser: boolean | null;
  hasPlaygroundAccess: boolean | null;
  setIsDeveloper: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIsAdvancedUser: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean | null>>;
  loadingApprovalStatus: boolean;
  refreshApprovalStatus: () => Promise<void>;
  dispatch: React.Dispatch<Action>;
  prompts: PromptConfig[] | null;
  fetchPrompts: (abortSignal: AbortSignal) => Promise<void>;
  savePrompt: (newPrompt: PromptConfig, abortSignal: AbortSignal) => Promise<void>;
  updatePrompt: (existingPrompt: PromptConfig, abortSignal: AbortSignal) => Promise<void>;
  deletePrompt: (prompt: PromptConfig, abortSignal: AbortSignal) => Promise<void>;
  initiatePrompt: (prompt: PromptConfig, abortSignal: AbortSignal) => Promise<void>;
  exportChat: (exportType: string, sessionId: string, abortSignal: AbortSignal) => Promise<void>;
  previewExport: (exportType: string, sessionId: string, abortSignal: AbortSignal) => Promise<string>;
  exportBattleCard: (sections: Array<{ agentName: string; title: string; content: string }>, sessionId: string, title: string, abortSignal: AbortSignal, options?: { singlePageRestriction?: boolean; allowTruncation?: boolean }) => Promise<void>;
  profilePhoto: string | null;
  setProfilePhoto: (photo: string | null) => void;
  faqs: FAQItem[];
  fetchFAQs: (abortSignal: AbortSignal) => Promise<void>;
  addFAQ: (faq: FAQItem, abortSignal: AbortSignal) => Promise<void>;
  updateFAQ: (faq: FAQItem, abortSignal: AbortSignal) => Promise<void>;
  deleteFAQ: (faq: FAQItem, abortSignal: AbortSignal) => Promise<void>;
  glossary: GlossaryItem[];
  fetchGlossary: (abortSignal: AbortSignal) => Promise<void>;
  addGlossary: (item: GlossaryItem, abortSignal: AbortSignal) => Promise<void>;
  updateGlossary: (item: GlossaryItem, abortSignal: AbortSignal) => Promise<void>;
  deleteGlossary: (item: GlossaryItem, abortSignal: AbortSignal) => Promise<void>;
  subscriptionVerified: boolean | null; // <-- Add this
  setSubscriptionVerified: React.Dispatch<React.SetStateAction<boolean | null>>; // <-- Add this
  refreshSubscriptionStatus: () => Promise<void>; // <-- Add this
  setShouldAddContinueOption: (value: boolean) => void;
  setMaxStep: (value: number) => void;
  setCurrentOrchestrationType: (value: OrchestrationType) => void;
}



export type UI = {
  title: string
  chat_title: string
  chat_description: string
  logo?: string
  chat_logo?: string
  favicon?: string
  ai_disclaimer?: string
  web_search_enabled: boolean
  info_button_top_left_title: string
  info_button_top_left_text: string
  info_button_top_middle_title: string
  info_button_top_middle_text: string
  info_button_top_right_title: string
  info_button_top_right_text: string
  info_button_bottom_left_title: string
  info_button_bottom_left_text: string
  info_button_bottom_middle_title: string
  info_button_bottom_middle_text: string
  info_button_bottom_right_title: string
  info_button_bottom_right_text: string
  speech_to_text_language: string
}

export type FrontendSettings = {
  auth_enabled?: string | null
  appInsights_connectionString?: string,
  microsoft_auth: string,
  ui?: UI
  upload_max_filesize?: number
  apiBaseUrl: string
  stripe_publishable_key?: string
}

export type UserInfo = {
  userId?: string; // Legacy property
  id?: string; // Current API property
  displayName?: string; // Legacy property
  name?: string; // Current API property
  email: string;
  subscribed: boolean;
  isSubscribed?: boolean;
  roles?: string[];
  provider?: string;
};
//define app context action types
export type Action =
  | { type: 'SET_ORCHESTRATION_STATE'; payload: OrchestrationState }
  | { type: 'CLEAR_ORCHSTATE_SUGGESTIONS'; }
  | { type: 'SET_CURRENT_AGENT'; payload: string }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'REPLACE_MESSAGE_ID'; payload: { oldId: string, newId: string } }
  | { type: 'UPDATE_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'CLEAR_CURRENT_CHAT' }
  | { type: 'SET_CHAT_HISTORY'; payload: ChatHistory }
  | { type: 'SET_CHAT_HISTORY_LOADING'; payload: boolean }
  | { type: 'SET_IS_LOADING'; payload: boolean }
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: Conversation }
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'REMOVE_SESSION_FROM_HISTORY'; payload: string }
  | { type: 'SET_ACTIVE_DOCUMENT'; payload: DocumentItem | null }
  | { type: 'SET_UPLOADING_STATUS'; payload: UploadingStatus }
  | { type: 'SET_IS_PHONE'; payload: boolean }
  | { type: 'SET_IS_CHROME_EXTENSION'; payload: boolean }
  | { type: 'SET_THEME_MODE'; payload: ThemeMode }
  | { type: 'ADD_PROMPT'; payload: PromptConfig }
  | { type: 'UPDATE_PROMPT'; payload: PromptConfig }
  | { type: 'DELETE_PROMPT'; payload: PromptConfig }
  | { type: 'SET_PROMPTS'; payload: PromptConfig[] } // Add prompts action type
  | { type: 'SET_SHOULD_ADD_CONTINUE_OPTION'; payload: boolean }
  | { type: 'SET_MAX_STEP'; payload: number }
  | { type: 'SET_CURRENT_ORCHESTRATION_TYPE'; payload: OrchestrationType }
  | { type: 'SET_READ_ONLY_MODE'; payload: boolean }

 
export interface FAQItem {
  id?: string;
  question: string;
  answer: string;
}

export interface GlossaryItem {
  id?: string;
  term: string;
  definition: string;
  tags?: string;
}

// BattleCard related interfaces
export interface BattleCardProps {
  open: boolean;
  onClose: () => void;
}

export interface ListItem {
    id: string;
    content: string;
    selected: boolean;
    subsection?: string; // Optional subsection header this item belongs to
}export interface AgentSection {
  title: string;
  content: string;
  selected: boolean;
  hasLists?: boolean;
  listItems?: ListItem[];
  processedContent?: string; // Content with list items removed for display
}

export interface AgentResponse {
  agentName: string;
  sections: AgentSection[];
  expanded: boolean;
}

export interface CurrentSection {
  title: string;
  content: string;
}

// Conversation Sharing and Metadata Types
export interface ConversationMetadata {
  sessionId: string;
  title?: string | null;
  ownerId: string;
  createdAt: string;
  lastModifiedAt: string;
  isOwner: boolean;
  isShared: boolean;
  sharedWith?: string[];
}

export interface ConversationListItem {
  sessionId: string;
  title?: string | null;
  ownerId: string;
  createdAt: string;
  lastModifiedAt: string;
  isOwner: boolean;
  isShared: boolean;
  sharedWith?: string[];
  currentAgent?: string | null;
  orchestrationCount: number;
}

export interface ConversationListResponse {
  ownedConversations: ConversationListItem[];
  sharedConversations: ConversationListItem[];
}

export interface ShareConversationRequest {
  sessionId: string;
  emailAddresses: string[];
}

export interface ShareConversationResponse {
  success: boolean;
  message: string;
  sharedWith: string[];
}

export interface GenerateConversationTitleRequest {
  sessionId: string;
}

export interface UpdateConversationTitleRequest {
  sessionId: string;
  title: string;
}
